import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withModifiers as _withModifiers, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-expanded", "aria-disabled"]
const _hoisted_2 = ["placeholder"]
const _hoisted_3 = {
  key: 0,
  class: "ss-dropdown",
  role: "listbox"
}
const _hoisted_4 = {
  key: 0,
  class: "ss-empty"
}
const _hoisted_5 = ["aria-selected", "onClick", "onMouseenter"]
const _hoisted_6 = ["innerHTML"]

import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, useSlots } from 'vue'

/**
 * Select pesquisavel e reutilizavel.
 * Substitui <select> nativo: v-model + lista de opcoes, filtrando enquanto o usuario digita.
 *
 * Dois modos de uso:
 * 1) Por props (recomendado quando ha uma lista de objetos):
 *    <SearchableSelect v-model="x" :options="perfis" option-value="perfilid" option-label="nome" />
 * 2) Drop-in do <select> nativo: basta trocar a tag e manter os <option> como slot.
 *    <SearchableSelect v-model="x"><option v-for="p in perfis" :value="p.id">{{ p.nome }}</option></SearchableSelect>
 *    Opcoes com atributo "disabled" viram o placeholder automaticamente.
 */

type Primitive = string | number | boolean | null
interface Option { value: Primitive; label: string; raw?: any }


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchableSelect',
  props: {
    modelValue: { type: [String, Number, Boolean, null] },
    options: {},
    optionValue: { default: 'value' },
    optionLabel: { default: 'label' },
    placeholder: { default: 'Selecione...' },
    searchPlaceholder: { default: '' },
    emptyText: { default: 'Nenhum resultado' },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    icon: { default: '' }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { emit: __emit }) {

const slots = useSlots()

// Extrai texto de children de vnode (string, numero ou arvore de vnodes)
const nodeText = (node: any): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join('')
  if (typeof node === 'object' && 'children' in node) return nodeText(node.children)
  return ''
}

// Le os <option> do slot default (modo drop-in). Reativo: a computed re-executa
// quando os dados reativos lidos pela render-function do slot mudam.
const parseSlotOptions = (): { options: Option[]; placeholder: string } => {
  const out: Option[] = []
  let placeholder = ''
  const walk = (nodes: any[]) => {
    for (const n of nodes || []) {
      if (!n || typeof n !== 'object') continue
      if (n.type === 'option') {
        const p = n.props || {}
        const label = nodeText(n.children).trim()
        const isDisabled = 'disabled' in p && p.disabled !== false && p.disabled != null
        if (isDisabled) {
          if (!placeholder) placeholder = label
          continue
        }
        out.push({ value: p.value !== undefined ? p.value : null, label })
      } else if (Array.isArray(n.children)) {
        walk(n.children)
      }
    }
  }
  try { walk(slots.default ? slots.default() : []) } catch { /* sem slot */ }
  return { options: out, placeholder }
}

const props = __props

const emit = __emit

const root = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const query = ref('')
const highlighted = ref(-1)

// Normaliza as opcoes: usa a prop :options se fornecida, senao le os <option> do slot.
const normalized = computed<Option[]>(() => {
  if (props.options && props.options.length) {
    return props.options.map((o) => {
      if (o !== null && typeof o === 'object') {
        return { value: o[props.optionValue], label: String(o[props.optionLabel] ?? ''), raw: o }
      }
      return { value: o as Primitive, label: String(o), raw: o }
    })
  }
  return parseSlotOptions().options
})

// Placeholder efetivo: prop explicita ou o texto da opcao "disabled" do slot
const effectivePlaceholder = computed(() => {
  if (props.placeholder && props.placeholder !== 'Selecione...') return props.placeholder
  const slotPh = parseSlotOptions().placeholder
  return slotPh || props.placeholder
})

const hasValue = computed(() => props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== '')

const selectedLabel = computed(() => {
  const match = normalized.value.find((o) => o.value === props.modelValue)
  return match ? match.label : ''
})

const searchPlaceholderText = computed(() =>
  props.searchPlaceholder || selectedLabel.value || effectivePlaceholder.value
)

// Filtro sem acento e case-insensitive
const normalizeStr = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const filtered = computed<Option[]>(() => {
  const q = normalizeStr(query.value.trim())
  if (!q) return normalized.value
  return normalized.value.filter((o) => normalizeStr(o.label).includes(q))
})

const isSelected = (opt: Option) => opt.value === props.modelValue

const highlightMatch = (label: string): string => {
  const q = query.value.trim()
  if (!q) return escapeHtml(label)
  const idx = normalizeStr(label).indexOf(normalizeStr(q))
  if (idx === -1) return escapeHtml(label)
  const before = escapeHtml(label.slice(0, idx))
  const match = escapeHtml(label.slice(idx, idx + q.length))
  const after = escapeHtml(label.slice(idx + q.length))
  return `${before}<mark>${match}</mark>${after}`
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const open = async () => {
  if (props.disabled) return
  isOpen.value = true
  query.value = ''
  highlighted.value = filtered.value.findIndex(isSelected)
  await nextTick()
  searchInput.value?.focus()
}

const close = () => {
  isOpen.value = false
  query.value = ''
  highlighted.value = -1
}

const toggle = () => (isOpen.value ? close() : open())

const select = (opt: Option) => {
  emit('update:modelValue', opt.value)
  emit('change', opt.value)
  close()
}

const clear = () => {
  emit('update:modelValue', null)
  emit('change', null)
}

const onControlKeydown = (e: KeyboardEvent) => {
  if (['Enter', ' ', 'ArrowDown'].includes(e.key) && !isOpen.value) {
    e.preventDefault()
    open()
  } else if (e.key === 'Escape') {
    close()
  }
}

const onSearchKeydown = (e: KeyboardEvent) => {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    highlighted.value = Math.min(highlighted.value + 1, filtered.value.length - 1)
    scrollToHighlighted()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    highlighted.value = Math.max(highlighted.value - 1, 0)
    scrollToHighlighted()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const opt = filtered.value[highlighted.value]
    if (opt) select(opt)
  } else if (e.key === 'Escape') {
    e.preventDefault()
    close()
  } else if (e.key === 'Tab') {
    close()
  }
}

const scrollToHighlighted = () => {
  nextTick(() => {
    const el = root.value?.querySelector('.ss-option.active') as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  })
}

// Reseta o highlight quando a lista filtrada muda
watch(filtered, () => {
  if (highlighted.value >= filtered.value.length) highlighted.value = filtered.value.length - 1
})

// Fecha ao clicar fora
const onClickOutside = (e: MouseEvent) => {
  if (root.value && !root.value.contains(e.target as Node)) close()
}

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: _normalizeClass(["searchable-select", { open: isOpen.value, disabled: _ctx.disabled }])
  }, [
    _createElementVNode("div", {
      class: "ss-control",
      role: "combobox",
      "aria-expanded": isOpen.value,
      "aria-haspopup": "listbox",
      "aria-disabled": _ctx.disabled,
      tabindex: "0",
      onClick: toggle,
      onKeydown: onControlKeydown
    }, [
      (_ctx.icon)
        ? (_openBlock(), _createElementBlock("i", {
            key: 0,
            class: _normalizeClass([_ctx.icon, "ss-icon"])
          }, null, 2))
        : _createCommentVNode("", true),
      (isOpen.value)
        ? _withDirectives((_openBlock(), _createElementBlock("input", {
            key: 1,
            ref_key: "searchInput",
            ref: searchInput,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
            class: "ss-search",
            placeholder: searchPlaceholderText.value,
            onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
            onKeydown: onSearchKeydown
          }, null, 40, _hoisted_2)), [
            [_vModelText, query.value]
          ])
        : (_openBlock(), _createElementBlock("span", {
            key: 2,
            class: _normalizeClass(["ss-value", { placeholder: !selectedLabel.value }])
          }, _toDisplayString(selectedLabel.value || effectivePlaceholder.value), 3)),
      (_ctx.clearable && hasValue.value && !_ctx.disabled)
        ? (_openBlock(), _createElementBlock("button", {
            key: 3,
            type: "button",
            class: "ss-clear",
            title: 'Limpar',
            onClick: _withModifiers(clear, ["stop"])
          }, _cache[2] || (_cache[2] = [
            _createElementVNode("i", { class: "fas fa-times" }, null, -1)
          ])))
        : _createCommentVNode("", true),
      _createElementVNode("i", {
        class: _normalizeClass(["fas fa-chevron-down ss-caret", { rotated: isOpen.value }])
      }, null, 2)
    ], 40, _hoisted_1),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
          (filtered.value.length === 0)
            ? (_openBlock(), _createElementBlock("li", _hoisted_4, _toDisplayString(_ctx.emptyText), 1))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filtered.value, (opt, i) => {
            return (_openBlock(), _createElementBlock("li", {
              key: i,
              role: "option",
              class: _normalizeClass(["ss-option", { active: i === highlighted.value, selected: isSelected(opt) }]),
              "aria-selected": isSelected(opt),
              onClick: ($event: any) => (select(opt)),
              onMouseenter: ($event: any) => (highlighted.value = i)
            }, [
              _createElementVNode("span", {
                innerHTML: highlightMatch(opt.label)
              }, null, 8, _hoisted_6)
            ], 42, _hoisted_5))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})